import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { useServices } from "@/app/use-services";
import { useMomentOnce } from "@/app/use-moment";
import { usePlan } from "@/features/plan/use-plan";
import { DayCalendar } from "@/features/forecast/DayCalendar";
import { AreaCards, DaysList, ReadingFootnote } from "@/features/forecast/ForecastCards";
import { errorMessage } from "@/lib/api/errors";
import type { Forecast, ForecastPeriod } from "@/lib/api/models/forecast";
import { Button, LinkButton } from "@/ui/Button";
import { Disclosure } from "@/ui/Disclosure";
import { LockIcon } from "@/ui/LockIcon";
import { ErrorView } from "@/ui/ErrorView";
import { Page } from "@/ui/Page";
import { Body, Display, Tiny } from "@/ui/Text";
import { LockedPreview } from "./LockedPreview";

const PERIODS: ReadonlyArray<{ key: ForecastPeriod; label: string; noun: string }> = [
  { key: "day", label: "Today", noun: "day" },
  { key: "week", label: "This week", noun: "week" },
  { key: "month", label: "This month", noun: "month" },
];

/** The server keeps a reading for the whole day; the client need not ask twice an hour. */
const STALE_MS = 30 * 60 * 1000;

const forecastKey = (period: ForecastPeriod) => ["me", "forecast", period] as const;

/**
 * Today, this week and this month, as the same cards first run showed: four
 * areas with a do, a don't and the planetary reason, plus the days to use.
 *
 * A locked account (free minutes spent, no plan) gets today's headline only,
 * with the rest of the reading and the week and month behind the plan.
 */
export function TodayTab() {
  const { api, analytics } = useServices();
  const navigate = useNavigate();
  const locked = usePlan().data?.locked === true;
  const [period, setPeriod] = useState<ForecastPeriod>("day");
  const queryClient = useQueryClient();
  const forecast = useQuery<Forecast>({
    queryKey: forecastKey(period),
    queryFn: () => api.fetchForecast(period),
    staleTime: STALE_MS,
  });
  const dayReady = period === "day" && forecast.isSuccess;
  useMomentOnce(forecast.isSuccess ? { name: "forecast viewed", period } : null);
  useMomentOnce(forecast.isError ? { name: "forecast failed", period } : null);

  // Each reading takes a few seconds to write the first time. Once the day is
  // on screen, the week and month are fetched quietly so a tab tap is instant.
  useEffect(() => {
    if (!dayReady || locked) return;
    for (const other of ["week", "month"] as const) {
      void queryClient.prefetchQuery({ queryKey: forecastKey(other), queryFn: () => api.fetchForecast(other), staleTime: STALE_MS });
    }
  }, [dayReady, locked, queryClient, api]);
  const noun = PERIODS.find((p) => p.key === period)?.noun ?? "day";

  return (
    <Page
      foot={
        locked ? undefined : (
        <>
          <Button
            label="Speak to Antar"
            onClick={() => {
              analytics.track({ name: "speak tapped", from: "today" });
              navigate("/talk/voice");
            }}
          />
          {forecast.data?.headline && (
            <div className="text-center">
              <LinkButton
                label="Share"
                onClick={() => {
                  analytics.track({ name: "forecast shared" });
                  void share(forecast.data.headline);
                }}
              />
            </div>
          )}
        </>
        )
      }
    >
      <PeriodTabs
        period={period}
        locked={locked}
        onChange={(next) => {
          if (locked && next !== "day") {
            analytics.track({ name: "locked period tapped", period: next });
            navigate("/subscribe");
            return;
          }
          setPeriod(next);
        }}
      />
      {forecast.isPending && <Body className="mt-6 text-mid">Reading your {noun}…</Body>}
      {forecast.isError && (
        <div className="mt-6">
          <ErrorView message={errorMessage(forecast.error)} onRetry={() => void forecast.refetch()} />
        </div>
      )}
      {forecast.data && <Reading forecast={forecast.data} onDetail={(detail) => analytics.track({ name: "forecast detail opened", detail })} />}
    </Page>
  );
}

function PeriodTabs({ period, locked, onChange }: { readonly period: ForecastPeriod; readonly locked: boolean; readonly onChange: (p: ForecastPeriod) => void }) {
  return (
    <div role="tablist" aria-label="Period" className="flex gap-5">
      {PERIODS.map((p) => (
        <button
          key={p.key}
          type="button"
          role="tab"
          aria-selected={p.key === period}
          onClick={() => onChange(p.key)}
          className={`label inline-flex min-h-11 items-center gap-1.5 ${p.key === period ? "font-semibold text-ink" : ""}`}
        >
          {p.label}
          {locked && p.key !== "day" && <LockIcon />}
        </button>
      ))}
    </div>
  );
}

/**
 * The headline and the four areas first. When to act sits underneath,
 * closed, with its key fact on the closed row; the tab already says which
 * period this is, so no date line repeats it above the headline.
 */
type Detail = "moon" | "days" | "why";

function Reading({ forecast, onDetail }: { readonly forecast: Forecast; readonly onDetail: (detail: Detail) => void }) {
  if (forecast.preview) {
    return (
      <>
        <Display className="mt-4">{forecast.headline}</Display>
        <LockedPreview />
      </>
    );
  }
  return (
    <>
      <Display className="mt-4">{forecast.headline}</Display>
      <AreaCards areas={forecast.areas} period={forecast.period} onWhyOpen={() => onDetail("why")} />
      <When forecast={forecast} onDetail={onDetail} />
      <ReadingFootnote period={forecast.period} />
    </>
  );
}

const MOON_VERDICT: Readonly<Record<NonNullable<Forecast["moon"]>["tone"], string>> = {
  good: "A good day",
  steady: "An ordinary day",
  careful: "Take it slow today",
};

function When({ forecast, onDetail }: { readonly forecast: Forecast; readonly onDetail: (detail: Detail) => void }) {
  if (forecast.moon) {
    return (
      <Disclosure title="The Moon today" hint={MOON_VERDICT[forecast.moon.tone]} onOpen={() => onDetail("moon")}>
        <Tiny>{forecast.moon.reason}</Tiny>
      </Disclosure>
    );
  }
  if (forecast.best_days.length === 0 && forecast.careful_days.length === 0) return null;
  const next = forecast.best_days[0];
  return (
    <Disclosure
      title="Best days and slow days"
      hint={next ? `Next best: ${next}` : `Take it slow: ${forecast.careful_days[0]}`}
      onOpen={() => onDetail("days")}
    >
      {forecast.days.length > 0 ? <DayCalendar days={forecast.days} /> : <DaysList best={forecast.best_days} careful={forecast.careful_days} />}
    </Disclosure>
  );
}

async function share(text: string) {
  try {
    if (navigator.share) await navigator.share({ text, url: "https://tryantar.com" });
    else await navigator.clipboard.writeText(text);
  } catch {
    // A dismissed share sheet is not an error.
  }
}
